/* Smartap motion (assets/st-motion.css says what moves and why).
   - Sections ease in: the top-level parts of every st- band after the page's first section are
     marked .st-rv only if they start below the viewport, then get .is-in when they come within
     reach. Nothing already on screen is hidden, nothing is hidden without this script, and focus
     reaching a hidden part shows it at once. Cards in a list get a small stagger.
   - Moment 1: the first section's main heading (its h1, or the homepage hero's heading) settles
     word by word, once per page per visit (sessionStorage), so a return visit is not made to wait;
     as one piece where splitting it into words would change its line breaks.
     Each word rises into place behind a mask with its top already showing, at full opacity, so
     the heading paints at once (no faint ghost words, no delay to the largest paint).
   Nothing runs under prefers-reduced-motion. */
/* Busy state on the site's own forms (quote quiz, contact, password page, article comments). Once a
   valid form is sent, its submit button reads "Sending…", is marked busy, and a second press is
   ignored, so a slow connection does not send the same request twice. Runs whatever the motion
   preference: it is state, not motion. The label comes from the script tag (st.sending, en and es).
   Shopify's bot protection (hCaptcha) cancels the submit click and submits the form itself later,
   so no submit event fires and the click always reads as prevented (measured 2026-09-26). The
   button is therefore marked from the form's own state just after the click: valid to the browser,
   no field our scripts marked invalid, no error summary showing. If the check shows a challenge
   and the visitor leaves it, the button is restored after 12s, so it cannot stay stuck. */
(() => {
  const label = document.currentScript?.dataset.sending || '';
  const FORMS = '.st-quiz__card, .st-contact__form, .st-password__form, .blog-post-comments__form-container form';

  const restore = (button) => {
    button.removeAttribute('aria-busy');
    delete button.closest('form')?.dataset.stSending;
    if (button.dataset.stLabel === undefined) return;
    if (button.tagName === 'BUTTON') button.textContent = button.dataset.stLabel;
    else button.value = button.dataset.stLabel;
    delete button.dataset.stLabel;
  };

  const busy = (button) => {
    const form = button.closest('form');
    form.dataset.stSending = 'true';
    button.setAttribute('aria-busy', 'true');
    if (label) {
      button.dataset.stLabel = button.tagName === 'BUTTON' ? button.textContent : button.value;
      if (button.tagName === 'BUTTON') button.textContent = label;
      else button.value = label;
    }
    setTimeout(() => form.dataset.stSending && restore(button), 12000);
  };

  window.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest?.('button[type="submit"], input[type="submit"]');
      const form = button?.closest(FORMS) || (button?.form?.matches(FORMS) ? button.form : null);
      if (!form) return;
      if (form.dataset.stSending) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      setTimeout(() => {
        const summary = form.querySelector('[data-st-summary]');
        const invalid = form.querySelector('[aria-invalid="true"]');
        if (!form.checkValidity() || invalid || (summary && !summary.hidden) || button.hidden) return;
        busy(button);
      }, 0);
    },
    true
  );
  // Coming back with the browser's back button restores the page as it was left: reset the button.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) document.querySelectorAll('[data-st-sending] [aria-busy="true"]').forEach(restore);
  });
})();

(() => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const main = document.getElementById('MainContent');
  if (!main) return;
  const sections = [...main.children].filter((el) => el.classList.contains('shopify-section'));

  function headline() {
    // The main heading's section: the first, or the second when a breadcrumb section comes first
    // (collection and product pages).
    const first = sections.slice(0, 2).find((sec) => sec.querySelector('h1, .st-hero h2'));
    const h = first?.querySelector('h1, .st-hero h2');
    if (!h || h.querySelector('.st-hw')) return;
    const key = `st-headline:${location.pathname}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    } catch (e) {}
    // Splitting into word boxes can change where a balanced heading breaks (measured at 375: one
    // more line, pushing the page down 42px). Measure before and after; if the height changes,
    // put the text back and settle the heading as one piece instead, so nothing moves.
    // Until the web fonts have loaded, the heading is set in its fallback, and word boxes can break
    // differently once the real face arrives (measured on a guide at 375: 0.012 layout shift). So
    // words are split only when fonts are already in (the usual case after the first page of a
    // visit); otherwise the heading rises as one piece, which cannot re-break.
    // A flex or grid heading (Horizon's cart title) would turn each word box into its own item.
    const layout = getComputedStyle(h).display;
    if (/flex|grid/.test(layout) || (document.fonts && document.fonts.status !== 'loaded')) {
      h.classList.add('st-headline-whole');
      return;
    }
    const before = h.getBoundingClientRect().height;
    const original = [...h.childNodes].map((c) => c.cloneNode(true));
    let n = 0;
    const walk = (node) => {
      [...node.childNodes].forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          const parts = child.textContent.split(/(\s+)/);
          if (parts.every((p) => !p.trim())) return;
          const frag = document.createDocumentFragment();
          parts.forEach((part) => {
            if (!part) return;
            if (!part.trim()) return frag.append(part);
            const span = document.createElement('span');
            span.className = 'st-hw';
            span.style.setProperty('--st-w', String(n++));
            span.textContent = part;
            frag.append(span);
          });
          child.replaceWith(frag);
        } else if (child.nodeType === Node.ELEMENT_NODE && !child.matches('svg, img')) {
          walk(child);
        }
      });
    };
    walk(h);
    if (Math.abs(h.getBoundingClientRect().height - before) > 1) {
      h.replaceChildren(...original);
      h.classList.add('st-headline-whole');
      return;
    }
    h.classList.add('st-headline-in');
  }

  function reveal() {
    if (!('IntersectionObserver' in window)) return;
    const vh = window.innerHeight;
    const parts = [];
    sections.slice(1).forEach((section) => {
      section.querySelectorAll('.st-band > .st-wrap').forEach((wrap) => {
        [...wrap.children].forEach((el, i) => {
          if (el.getBoundingClientRect().top < vh) return;
          el.style.setProperty('--st-i', String(Math.min(i, 3)));
          el.classList.add('st-rv');
          el.querySelectorAll(':scope > li, :scope > [role="list"] > li, :scope > ul > li').forEach((li, j) => {
            if (j > 5) return;
            li.classList.add('st-rv');
            li.style.setProperty('--st-i', String(j + 1));
          });
          parts.push(el);
        });
      });
    });
    if (!parts.length) return;
    const show = (el, now) => {
      const all = [el, ...el.querySelectorAll('.st-rv')];
      if (now) all.forEach((c) => c.classList.add('st-rv--now'));
      all.forEach((c) => c.classList.add('is-in'));
    };
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          show(en.target);
          io.unobserve(en.target);
        });
      },
      { rootMargin: '0px' }
    );
    parts.forEach((el) => io.observe(el));
    // Keyboard users never meet a hidden control: focus shows its part at once.
    document.addEventListener('focusin', (ev) => {
      const part = ev.target.closest?.('.st-rv:not(.is-in)');
      // At once, not faded: the focus ring must be visible on the first frame.
      if (part) show(part.closest('.st-band > .st-wrap > .st-rv') || part, true);
    });
  }

  // v5's section moments: every section heading below the first screen rises word by word as it
  // comes into view, and the big pictures wipe open from the bottom while settling from a slight
  // zoom. The same safety as the main heading: split only once fonts are in and only if the words
  // keep the heading's height; otherwise it rises as one piece.
  function rises() {
    if (!('IntersectionObserver' in window)) return;
    const vh = window.innerHeight;
    const heads = sections.slice(1).flatMap((sec) => [...sec.querySelectorAll('.st-band .st-h')]);
    const wipes = sections.slice(1).flatMap((sec) => [...sec.querySelectorAll('.st-fmt__ph, .st-app__stage, .st-demo, .st-cta-band__media, .st-split__media')]);
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          io.unobserve(en.target);
          const t = en.target;
          t.querySelectorAll(':scope > .st-rise').forEach((h) => h.classList.add('st-rise-in'));
          if (t.matches('.st-band')) t.querySelectorAll('.st-wipe').forEach((w) => w.classList.add('st-wipe-in'));
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
    );
    heads.forEach((h) => {
      if (h.getBoundingClientRect().top < vh || h.querySelector('.st-hw')) return;
      const whole = /flex|grid/.test(getComputedStyle(h).display) || (document.fonts && document.fonts.status !== 'loaded');
      if (!whole) {
        const before = h.getBoundingClientRect().height;
        const original = [...h.childNodes].map((c) => c.cloneNode(true));
        let n = 0;
        const walk = (node) => {
          [...node.childNodes].forEach((child) => {
            if (child.nodeType === Node.TEXT_NODE) {
              const parts = child.textContent.split(/(\s+)/);
              if (parts.every((p) => !p.trim())) return;
              const frag = document.createDocumentFragment();
              parts.forEach((part) => {
                if (!part) return;
                if (!part.trim()) return frag.append(part);
                const span = document.createElement('span');
                span.className = 'st-hw';
                span.style.setProperty('--st-w', String(n++));
                span.textContent = part;
                frag.append(span);
              });
              child.replaceWith(frag);
            } else if (child.nodeType === Node.ELEMENT_NODE && !child.matches('svg, img')) walk(child);
          });
        };
        walk(h);
        if (Math.abs(h.getBoundingClientRect().height - before) > 1) h.replaceChildren(...original);
      }
      h.classList.add('st-rise');
      // Watched through its parent: a heading clipped to nothing never reads as on screen.
      if (h.parentElement) {
        h.parentElement.dataset.stRiseHost = '';
        io.observe(h.parentElement);
      }
    });
    // A clipped element reads as never visible, and some sit in display: contents panels or in a
    // format tab not yet shown, so the wipes are watched through their section, all at once.
    const hosts = new Set();
    wipes.forEach((w) => {
      const host = w.closest('.st-band');
      if (!host || host.getBoundingClientRect().top < vh) return;
      w.classList.add('st-wipe');
      hosts.add(host);
    });
    hosts.forEach((host) => io.observe(host));
    // A fast flick can carry a heading or picture past the screen between two frames, where the
    // observer never sees it; anything already above the bottom of the screen is shown at once.
    const pending = () => [...document.querySelectorAll('.st-rise:not(.st-rise-in)'), ...hosts];
    let tick = false;
    const sweep = () => {
      tick = false;
      pending().forEach((el) => {
        if (el.getBoundingClientRect().top > window.innerHeight) return;
        if (el.classList.contains('st-rise')) el.classList.add('st-rise-in');
        else {
          el.querySelectorAll('.st-wipe').forEach((w) => w.classList.add('st-wipe-in'));
          hosts.delete(el);
        }
      });
    };
    const onScroll = () => {
      if (tick) return;
      tick = true;
      requestAnimationFrame(sweep);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    document.querySelector('.page-wrapper')?.addEventListener('scroll', onScroll, { passive: true });
    // Focus inside a part still hidden shows it at once.
    document.addEventListener('focusin', (ev) => {
      ev.target.closest?.('.st-wipe:not(.st-wipe-in)')?.classList.add('st-wipe-in');
    });
  }

  const run = () => {
    headline();
    reveal();
    // After the web fonts, so headings split into words without re-breaking; at most a second.
    let done = false;
    const go = () => {
      if (done) return;
      done = true;
      rises();
    };
    document.fonts?.ready.then(go);
    setTimeout(go, 1000);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
})();
